import React, { useState, useEffect } from "react";
import { apiService } from "../../../services/apiService";
import { Button, ImageWithFallback } from "../../components/Utilities";
import Delete from "../../assets/Delete.svg";
import { Loading } from "../../components/Loading";
import "./UploadPicturesModel.css";

export const UploadModal = ({ isOpen, onClose, car, onSubmitPictures }) => {
  const [primaryImage, setPrimaryImage] = useState(null);
  const [primaryViewType, setPrimaryViewType] = useState("");
  const [additionalImages, setAdditionalImages] = useState([]);
  const [viewTypes, setViewTypes] = useState([]);
  const [error, setError] = useState(null);

  // Working copy of evaluation images
  const [evaluationImages, setEvaluationImages] = useState([]);
  // Original images to revert on close
  const [originalEvaluationImages, setOriginalEvaluationImages] = useState([]);

  const [isEvaluationSubmitted, setIsEvaluationSubmitted] = useState(false);
  const [isImagesUploaded, setIsImagesUploaded] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadTriggered, setUploadTriggered] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const viewOptions = ["Left", "Right", "Rear", "Inside First row", "Inside Boot"];

  // Reset and initialize on open/close
  useEffect(() => {
    if (isOpen && car.id) {
      // Save original for revert
      const originalImages = car.evaluationImages || [];
      setOriginalEvaluationImages(originalImages);
      setEvaluationImages([...originalImages]);

      setPrimaryImage(null);
      setPrimaryViewType("");
      setAdditionalImages([]);
      setViewTypes([]);
      setError(null);
      setIsEvaluationSubmitted(car.state === "EVALUATION_COMPLETE");
      setIsImagesUploaded(originalImages.length > 0);

      const fetchCarDetails = async () => {
        setActionLoading(true);
        try {
          const response = await apiService.getCarDetailsByFlowEntity(car.flowEntityId);
          const fetchedImages = (response.data?.car.evaluationImages || []).filter(
            (img) => img && img.id && img.url && typeof img.angle === "string"
          );

          setOriginalEvaluationImages(fetchedImages);
          setEvaluationImages([...fetchedImages]);
          setIsEvaluationSubmitted(response.data?.state === "EVALUATION_COMPLETE");
          setIsImagesUploaded(fetchedImages.length > 0);

          const frontFace = fetchedImages.find((img) => img.angle === "Front Face");
          if (frontFace) {
            setPrimaryImage(frontFace);
            setPrimaryViewType("Front Face");
          }
        } catch (err) {
          console.error("Error fetching car details:", err);
          setError("Failed to load car details.");
        } finally {
          setActionLoading(false);
        }
      };

      fetchCarDetails();
    } else {
      // Reset all on close
      setPrimaryImage(null);
      setPrimaryViewType("");
      setAdditionalImages([]);
      setViewTypes([]);
      setError(null);
      setEvaluationImages([]);
      setOriginalEvaluationImages([]);
      setIsEvaluationSubmitted(false);
      setIsImagesUploaded(false);
      setUploadTriggered(false);
      setIsImageModalOpen(false);
      setSelectedImage(null);
    }
  }, [isOpen, car.id, car.flowEntityId]);

  // Convert file to base64 (without prefix)
  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      if (!(file instanceof File)) {
        reject(new Error("Invalid file input"));
        return;
      }
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result?.split(",")[1];
        result ? resolve(result) : reject(new Error("Base64 conversion failed"));
      };
      reader.onerror = reject;
    });

  // Validate file
  const validateFile = (file) => {
    if (!(file instanceof File)) return "Invalid file.";
    // const allowed = ["image/jpeg", "image/png", "image/svg+xml"];
    // if (!allowed.includes(file.type)) return `Invalid type: ${file.type}`;
    // if (file.size > 5 * 1024 * 1024) return "File > 5MB";
    return null;
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file || uploadTriggered) return;

    const err = validateFile(file);
    if (err) {
      setError(err);
      return;
    }

    setActionLoading(true);
    setUploadTriggered(true);

    if (primaryImage?.url) URL.revokeObjectURL(primaryImage.url);

    const url = URL.createObjectURL(file);
    const newImg = { id: Date.now(), url, angle: "Front Face", file };
    setPrimaryImage(newImg);
    setPrimaryViewType("Front Face");
    setError(null);

    setTimeout(() => {
      setUploadTriggered(false);
      setActionLoading(false);
    }, 300);
  };

  const handleAddMorePictures = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const err = validateFile(file);
    if (err) {
      setError(err);
      return;
    }

    setActionLoading(true);

    const url = URL.createObjectURL(file);
    const newImg = { id: Date.now(), url, angle: "", file };
    setAdditionalImages((prev) => [...prev, newImg]);
    setViewTypes((prev) => [...prev, ""]);
    setError(null);
    event.target.value = null;

    setTimeout(() => setActionLoading(false), 300);
  };

  const handleViewTypeChange = (index, value) => {
    setViewTypes((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
    setAdditionalImages((prev) => {
      const updated = [...prev];
      updated[index].angle = value;
      return updated;
    });
  };

  const handleDeleteImage = (imageId, type) => {
    setActionLoading(true);

    try {
      if (type === "primary") {
        if (primaryImage?.url) URL.revokeObjectURL(primaryImage.url);
        setPrimaryImage(null);
        setPrimaryViewType("");
        const newList = evaluationImages.filter((img) => img?.angle !== "Front Face");
        setEvaluationImages(newList);
        setIsImagesUploaded(newList.length > 0);
        onSubmitPictures(car.id, newList);
      } else if (type === "additional") {
        const idx = additionalImages.findIndex((img) => img.id === imageId);
        if (additionalImages[idx]?.url) URL.revokeObjectURL(additionalImages[idx].url);
        setAdditionalImages((prev) => prev.filter((img) => img.id !== imageId));
        setViewTypes((prev) => prev.filter((_, i) => i !== idx));
      } else if (type === "evaluation") {
        const newList = evaluationImages.filter((img) => img?.id !== imageId);
        setEvaluationImages(newList);
        setIsImagesUploaded(newList.length > 0);
        onSubmitPictures(car.id, newList);
      }
      setError(null);
    } catch (err) {
      console.error("Delete failed:", err);
      setError("Failed to delete image.");
    } finally {
      setTimeout(() => setActionLoading(false), 300);
    }
  };

  const handleUploadSingle = async (image, angle) => {
    if (!image?.file || !angle) return null;

    try {
      const base64 = await toBase64(image.file);
      const payload = {
        file: base64,
        folder: "evaluations",
        fileName: `${angle}_${Date.now()}.jpg`,
        metadata: {
          contentType: image.file.type || "image/jpeg",
          originalName: image.file.name,
          size: image.file.size,
          carId: car.id,
          angle,
        },
      };

      const res = await apiService.uploadFile(payload);
      return {
        id: Date.now(),
        angle,
        uploadedAt: new Date().toISOString(),
        url: res.data?.publicUrl || "",
      };
    } catch (err) {
      setError(err.response?.data?.error || "Upload failed");
      return null;
    }
  };

  const handleSubmitEvaluation = async () => {
    if (additionalImages.some((img) => !img.angle)) {
      setError("Select view type for all images.");
      return;
    }

    const hasFrontFace = !!primaryImage || evaluationImages.some((img) => img?.angle === "Front Face");
    if (!hasFrontFace && additionalImages.length === 0) {
      setError("Front Face image is mandatory.");
      return;
    }

    setActionLoading(true);
    setIsUploading(true);

    try {
      let newList = [...evaluationImages];
      const uploadedNew = [];

      // Upload primary
      if (primaryImage) {
        const uploaded = await handleUploadSingle(primaryImage, "Front Face");
        if (uploaded) {
          newList = newList.filter((img) => img?.angle !== "Front Face");
          newList.unshift(uploaded);
          URL.revokeObjectURL(primaryImage.url);
        }
      }

      // Upload additional
      for (let i = 0; i < additionalImages.length; i++) {
        const uploaded = await handleUploadSingle(additionalImages[i], additionalImages[i].angle);
        if (uploaded) {
          uploadedNew.push(uploaded);
          URL.revokeObjectURL(additionalImages[i].url);
        }
      }

      newList = [...newList, ...uploadedNew];

      // Update state
      setEvaluationImages(newList);
      setOriginalEvaluationImages(newList); // Update original on success
      setPrimaryImage(null);
      setAdditionalImages([]);
      setViewTypes([]);
      setIsImagesUploaded(newList.length > 0);

      // Submit to backend
      await apiService.submitEvaluation({
        flowEntityId: car.flowEntityId,
        evaluationImages: newList,
      });

      setIsEvaluationSubmitted(true);
      onSubmitPictures(car.id, newList);
      onClose();
    } catch (err) {
      setError("Submission failed. Try again.");
    } finally {
      setIsUploading(false);
      setTimeout(() => setActionLoading(false), 300);
    }
  };

  const handleImageClick = (image) => {
    setSelectedImage(image);
    setIsImageModalOpen(true);
  };

  const handleCloseImageModal = () => {
    setIsImageModalOpen(false);
    setSelectedImage(null);
  };

  // Revert on close
  const handleModalClose = () => {
    setEvaluationImages([...originalEvaluationImages]);
    onSubmitPictures(car.id, originalEvaluationImages);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Global Loading Overlay */}
      {actionLoading && <Loading />}

      <div className="upload-modal">
        <div className="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="model-header">
              <p>
                {console.log(isEvaluationSubmitted , evaluationImages?.length,"loof")}
                
                {isEvaluationSubmitted || evaluationImages?.length
                  ? "View Pictures"
                  : "Upload Pictures"}
              </p>
              <button
                className="modal-close"
                onClick={handleModalClose}
                disabled={actionLoading}
              >
                ×
              </button>
            </div>

            <div className="model-body">
              <div className="right-part">
                <div className="modal-title">
                  <h4 className="car-title-modal">
                    {car.year} {car.brand} {car.model}
                  </h4>
                  <p className="car-number">{car.rto}</p>
                </div>
                <p className="mandatory-txt">
                  Mandatory for Bidding <br /> Front View with Number plate*
                </p>
                <div className="upload-pic">
                  <input
                    type="file"
                    id="primaryInput"
                    onChange={handleFileUpload}
                    style={{ display: "none" }}
                    disabled={actionLoading}
                  />
                </div>
                <div className="profile-pic">
                  {primaryImage ? (
                    <div key={primaryImage.id}>
                      <ImageWithFallback
                        src={primaryImage.url}
                        alt="Primary"
                        className="preview-image"
                        onClick={() => handleImageClick(primaryImage)}
                      />
                      <img
                        src={Delete}
                        alt="Delete"
                        className="delete-icon"
                        onClick={() => handleDeleteImage(primaryImage.id, "primary")}
                      />
                    </div>
                  ) : evaluationImages.find((img) => img?.angle === "Front Face") ? (
                    <div key={evaluationImages.find((img) => img?.angle === "Front Face").id}>
                      <ImageWithFallback
                        src={evaluationImages.find((img) => img?.angle === "Front Face").url}
                        alt="Primary"
                        className="preview-image"
                        onClick={() => handleImageClick(evaluationImages.find((img) => img?.angle === "Front Face"))}
                      />
                      <img
                        src={Delete}
                        alt="Delete"
                        className="delete-icon"
                        onClick={() =>
                          handleDeleteImage(
                            evaluationImages.find((img) => img?.angle === "Front Face").id,
                            "evaluation"
                          )
                        }
                      />
                    </div>
                  ) : (
                    <Button
                      variant="primary"
                      onClick={() => document.getElementById("primaryInput").click()}
                      className="upload-button"
                      disabled={actionLoading}
                    >
                      {actionLoading ? "Uploading..." : "Upload Pictures"}
                    </Button>
                  )}
                </div>
              </div>

              <div className="left-part">
                <div className="additional-images">
                  {additionalImages.map((image, index) => (
                    <div className="add-image-holder" key={image.id}>
                      <div className="image-container">
                        <img
                          src={image.url}
                          alt={`Additional ${index + 1}`}
                          className="preview-image"
                          onClick={() => handleImageClick(image)}
                        />
                        <ImageWithFallback
                          src={Delete}
                          alt="Delete"
                          className="delete-add-icon"
                          onClick={() => handleDeleteImage(image.id, "additional")}
                        />
                      </div>
                      <select
                        value={viewTypes[index] || ""}
                        onChange={(e) => handleViewTypeChange(index, e.target.value)}
                        className="view-type-dropdown"
                        disabled={actionLoading}
                      >
                        <option value="" disabled>Select View Type</option>
                        {viewOptions.map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>
                  ))}

                  {evaluationImages
                    .filter((img) => img?.angle && img.angle !== "Front Face")
                    .map((image) => (
                      <div className="add-image-holder" key={image.id}>
                        <div className="image-container">
                          <img
                            src={image.url}
                            alt="Uploaded"
                            className="preview-image"
                            onClick={() => handleImageClick(image)}
                          />
                          <ImageWithFallback
                            src={Delete}
                            alt="Delete"
                            className="delete-add-icon"
                            onClick={() => handleDeleteImage(image.id, "evaluation")}
                          />
                        </div>
                        <p>{image.angle}</p>
                      </div>
                    ))}
                </div>

                {error && <p className="error-text">{error}</p>}

                <div className="add-more-btns">
                  <div className="add-more-container">
                    <Button
                      variant="primary"
                      onClick={() => document.getElementById("additionalInput").click()}
                      className="add-more-btn"
                      disabled={actionLoading || !primaryImage}
                    >
                      + Add more Pictures
                    </Button>
                    <input
                      type="file"
                      id="additionalInput"
                      onChange={handleAddMorePictures}
                      style={{ display: "none" }}
                      disabled={actionLoading}
                    />
                  </div>

                  <div className="modal-buttons">
                    <Button
                      variant="primary"
                      onClick={handleSubmitEvaluation}
                      disabled={isUploading || !primaryImage || actionLoading}
                      className="sub-btn-pictures"
                    >
                      {isUploading || actionLoading ? "Submitting..." : "Submit Evaluation"}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Full-size Image Modal */}
      {isImageModalOpen && selectedImage && (
        <div className="image-modal-overlay" onClick={handleCloseImageModal}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="image-modal-close" onClick={handleCloseImageModal}>
              ×
            </button>
            <img src={selectedImage.url} alt="Full" className="full-size-image" />
          </div>
        </div>
      )}
    </>
  );
};